import type {
  SellSummaryResponse,
  UpsellResponse,
  WinbackBatchSummary,
  WinbackBatchView,
} from "../sell-types";
import type { SavedCardInfo } from "../types";
import type {
  PaymentSummary,
  ReconciliationIssue,
  RefundRequestResult,
  RefundResult,
} from "../payment-types";
import type { EcommercePackItem } from "../../features/automations/ecommerce/pack-keys";
import type { OrderLifecycleSettings, OrderNotificationConfig } from "../order-lifecycle-types";
import type { QuoteApprovalItem, QuoteListPage, QuoteSettings, QuoteSummary } from "../quote-types";
import type {
  CartApprovalItem,
  CartAuditEntry,
  CartLine,
  CartLockResult,
  CartSendResult,
  CartSummary,
  DraftOrderField,
  DraftOrderOutcome,
  PickerProduct,
} from "../cart-types";
import type { AbTestResultsView, AbTestSummary } from "../ab-test-types";
import type {
  ManualPaymentView,
  PhotoMatchView,
  ReceiptOcrResultView,
} from "../manual-payment-types";
import { ApiError } from "../errors";
import type { ApiClient } from "../api-client";
import type { HttpTransport } from "../http/transport";

export type SellApiMethods = Pick<
  ApiClient,
  | "getUpsellSuggestions"
  | "getCouponCapability"
  | "applyCartCoupon"
  | "removeCartCoupon"
  | "listWinbackBatches"
  | "getWinbackBatch"
  | "approveWinbackItems"
  | "rejectWinbackItems"
  | "editWinbackItem"
  | "getSellSummary"
  | "listCartProducts"
  | "findOpenCart"
  | "getOrCreateCart"
  | "applyCartDiscount"
  | "acquireCartLock"
  | "releaseCartLock"
  | "setCartNotes"
  | "listCartAudit"
  | "sendCart"
  | "convertCartToQuote"
  | "convertCartToPaymentLink"
  | "createManualPaymentLink"
  | "createWebFormLink"
  | "cartQrUrl"
  | "decideCartDiscount"
  | "listManualPayments"
  | "verifyManualPayment"
  | "startManualPayment"
  | "listPhotoMatches"
  | "listReceiptOcrResults"
  | "approveReceiptOcrResult"
  | "rejectReceiptOcrResult"
  | "listCartOverview"
  | "listCartApprovals"
  | "createDraftOrder"
  | "addCartItem"
  | "createQuote"
  | "listPayments"
  | "requestRefund"
  | "refundPayment"
  | "listReconciliationIssues"
  | "resolveReconciliationIssue"
  | "getQuote"
  | "quotePdfUrl"
  | "quoteDocumentLink"
  | "resendQuote"
  | "transitionQuote"
  | "listContactQuotes"
  | "listQuotes"
  | "applyQuoteDiscount"
  | "decideQuoteDiscount"
  | "listQuoteApprovals"
  | "quoteFromCart"
  | "addQuoteItem"
  | "updateQuoteItem"
  | "removeQuoteItem"
  | "updateQuote"
  | "listEcommerceAutomations"
  | "setEcommerceAutomation"
  | "listOrderNotificationConfigs"
  | "saveOrderNotificationConfig"
  | "getOrderLifecycleSettings"
  | "updateOrderLifecycleSettings"
  | "getQuoteSettings"
  | "updateQuoteSettings"
  | "saveCartItemForLater"
  | "restoreCartItem"
  | "createAbTest"
  | "getAbTestResults"
  | "promoteAbTestWinner"
  | "removeCartItem"
  | "listPaymentMethods"
  | "makePaymentMethodDefault"
  | "removePaymentMethod"
>;

export function createSellApi({ request, ws, base }: HttpTransport): SellApiMethods {
  return {
    async getUpsellSuggestions(id, cartId) {
      return request<UpsellResponse>(
        "GET",
        `${ws(id)}/sell/upsell-suggestions?cartId=${encodeURIComponent(cartId)}`,
      );
    },
    async getCouponCapability(id) {
      return request<{ couponValidation: boolean }>("GET", `${ws(id)}/carts/coupon-capability`);
    },
    async applyCartCoupon(id, cartId, code) {
      return (
        await request<{ cart: CartSummary }>(
          "POST",
          `${ws(id)}/carts/${encodeURIComponent(cartId)}/coupon`,
          { code },
        )
      ).cart;
    },
    async removeCartCoupon(id, cartId) {
      return (
        await request<{ cart: CartSummary }>(
          "DELETE",
          `${ws(id)}/carts/${encodeURIComponent(cartId)}/coupon`,
        )
      ).cart;
    },
    async listWinbackBatches(id) {
      return (await request<{ batches: WinbackBatchSummary[] }>("GET", `${ws(id)}/sell/winback`))
        .batches;
    },
    getWinbackBatch: (id, batchId) =>
      request<WinbackBatchView>("GET", `${ws(id)}/sell/winback/${encodeURIComponent(batchId)}`),
    async approveWinbackItems(id, batchId, itemIds) {
      await request("POST", `${ws(id)}/sell/winback/${encodeURIComponent(batchId)}/approve`, {
        itemIds,
      });
    },
    async rejectWinbackItems(id, batchId, itemIds) {
      await request("POST", `${ws(id)}/sell/winback/${encodeURIComponent(batchId)}/reject`, {
        itemIds,
      });
    },
    async editWinbackItem(id, batchId, itemId, draftCopy) {
      await request(
        "PATCH",
        `${ws(id)}/sell/winback/${encodeURIComponent(batchId)}/items/${encodeURIComponent(itemId)}`,
        { draftCopy },
      );
    },
    async getSellSummary(id, days) {
      return request<SellSummaryResponse>("GET", `${ws(id)}/analytics/sell/summary?days=${days}`);
    },
    async listCartProducts(id, filters = {}) {
      const q = new URLSearchParams();
      if (filters.search) q.set("search", filters.search);
      if (filters.category) q.set("category", filters.category);
      const qs = q.size > 0 ? `?${q.toString()}` : "";
      return (await request<{ products: PickerProduct[] }>("GET", `${ws(id)}/cart-products${qs}`))
        .products;
    },
    async findOpenCart(id, contactId) {
      const { carts } = await request<{ carts: CartSummary[] }>(
        "GET",
        `${ws(id)}/carts?contactId=${encodeURIComponent(contactId)}`,
      );
      return carts.find((c) => c.status === "open") ?? null;
    },
    async getOrCreateCart(id, input) {
      return (await request<{ cart: CartSummary }>("POST", `${ws(id)}/carts`, input)).cart;
    },
    async applyCartDiscount(id, cartId, input) {
      return (
        await request<{ cart: CartSummary }>(
          "POST",
          `${ws(id)}/carts/${encodeURIComponent(cartId)}/discount`,
          input,
        )
      ).cart;
    },
    async acquireCartLock(id, cartId) {
      return request<CartLockResult>("POST", `${ws(id)}/carts/${encodeURIComponent(cartId)}/lock`);
    },
    async releaseCartLock(id, cartId) {
      await request<void>("DELETE", `${ws(id)}/carts/${encodeURIComponent(cartId)}/lock`);
    },
    async setCartNotes(id, cartId, input) {
      return (
        await request<{ cart: CartSummary }>(
          "PUT",
          `${ws(id)}/carts/${encodeURIComponent(cartId)}/notes`,
          input,
        )
      ).cart;
    },
    async listCartAudit(id, cartId) {
      return (
        await request<{ entries: CartAuditEntry[] }>(
          "GET",
          `${ws(id)}/carts/${encodeURIComponent(cartId)}/audit`,
        )
      ).entries;
    },
    async sendCart(id, cartId, input) {
      return request<CartSendResult>(
        "POST",
        `${ws(id)}/carts/${encodeURIComponent(cartId)}/send`,
        input,
      );
    },
    convertCartToQuote(id, cartId) {
      return request("POST", `${ws(id)}/carts/${encodeURIComponent(cartId)}/convert-to-quote`, {});
    },
    convertCartToPaymentLink(id, cartId) {
      return request(
        "POST",
        `${ws(id)}/carts/${encodeURIComponent(cartId)}/convert-to-payment-link`,
        {},
      );
    },
    createWebFormLink(id, conversationId, kind) {
      return request(
        "POST",
        `${ws(id)}/conversations/${encodeURIComponent(conversationId)}/web-link`,
        {
          kind,
        },
      );
    },
    createManualPaymentLink(id, input) {
      return request("POST", `${ws(id)}/payment-links/manual`, input);
    },
    cartQrUrl(id, cartId) {
      return `${base}${ws(id)}/carts/${encodeURIComponent(cartId)}/qr`;
    },
    async decideCartDiscount(id, cartId, decision, note) {
      return (
        await request<{ cart: CartSummary }>(
          "POST",
          `${ws(id)}/carts/${encodeURIComponent(cartId)}/${decision}`,
          note ? { note } : {},
        )
      ).cart;
    },
    async listManualPayments(id, status) {
      const query = status ? `?status=${encodeURIComponent(status)}` : "";
      return (
        await request<{ manualPayments: ManualPaymentView[] }>(
          "GET",
          `${ws(id)}/manual-payments${query}`,
        )
      ).manualPayments;
    },
    async startManualPayment(id, orderId, input) {
      return (
        await request<{ manualPayment: ManualPaymentView }>(
          "POST",
          `${ws(id)}/orders/${encodeURIComponent(orderId)}/manual-payment`,
          { orderType: "order", amountMinor: input.amountMinor, currency: input.currency },
        )
      ).manualPayment;
    },
    async verifyManualPayment(id, orderId, input) {
      return (
        await request<{ manualPayment: ManualPaymentView }>(
          "POST",
          `${ws(id)}/orders/${encodeURIComponent(orderId)}/verify-payment`,
          input,
        )
      ).manualPayment;
    },
    async listPhotoMatches(id, messageIds) {
      return (
        await request<{ results: PhotoMatchView[] }>(
          "GET",
          `${ws(id)}/photo-matches?messageIds=${encodeURIComponent(messageIds.join(","))}`,
        )
      ).results;
    },
    async listReceiptOcrResults(id, messageIds) {
      return (
        await request<{ results: ReceiptOcrResultView[] }>(
          "GET",
          `${ws(id)}/receipt-ocr-results?messageIds=${encodeURIComponent(messageIds.join(","))}`,
        )
      ).results;
    },
    async approveReceiptOcrResult(id, resultId, orderId) {
      return (
        await request<{ result: ReceiptOcrResultView }>(
          "POST",
          `${ws(id)}/receipt-ocr-results/${encodeURIComponent(resultId)}/approve`,
          { orderId },
        )
      ).result;
    },
    async rejectReceiptOcrResult(id, resultId) {
      return (
        await request<{ result: ReceiptOcrResultView }>(
          "POST",
          `${ws(id)}/receipt-ocr-results/${encodeURIComponent(resultId)}/reject`,
          {},
        )
      ).result;
    },
    async listCartOverview(id, tab) {
      return (await request<{ carts: CartSummary[] }>("GET", `${ws(id)}/cart-overview?tab=${tab}`))
        .carts;
    },
    async listCartApprovals(id) {
      return (await request<{ approvals: CartApprovalItem[] }>("GET", `${ws(id)}/cart-approvals`))
        .approvals;
    },
    async createDraftOrder(id, conversationId): Promise<DraftOrderOutcome> {
      try {
        const r = await request<{ cart: CartSummary }>(
          "POST",
          `${ws(id)}/conversations/${encodeURIComponent(conversationId)}/draft-order`,
          {},
        );
        return { status: "draft", cart: r.cart };
      } catch (e) {
        if (e instanceof ApiError && e.code === "DRAFT_ORDER_INCOMPLETE") {
          const d = e.details as {
            missing?: DraftOrderField[];
            unmatched?: string[];
          };
          return {
            status: "incomplete",
            missing: d.missing ?? [],
            unmatched: d.unmatched ?? [],
          };
        }
        if (e instanceof ApiError && e.code === "AI_UNAVAILABLE")
          return { status: "ai_unavailable" };
        throw e;
      }
    },
    async addCartItem(id, cartId, input) {
      return (
        await request<{ item: CartLine }>(
          "POST",
          `${ws(id)}/carts/${encodeURIComponent(cartId)}/items`,
          input,
        )
      ).item;
    },
    async createQuote(id, input) {
      return (await request<{ quote: QuoteSummary }>("POST", `${ws(id)}/quotes`, input)).quote;
    },
    async listPayments(id, subject) {
      const query = subject
        ? `?relatedType=${subject.relatedType}&relatedId=${encodeURIComponent(subject.relatedId)}`
        : "";
      return (await request<{ payments: PaymentSummary[] }>("GET", `${ws(id)}/payments${query}`))
        .payments;
    },
    requestRefund: (id, paymentId, input) =>
      request<RefundRequestResult>(
        "POST",
        `${ws(id)}/payments/${encodeURIComponent(paymentId)}/refund-requests`,
        input,
      ),
    refundPayment: (id, paymentId, input) =>
      request<RefundResult>(
        "POST",
        `${ws(id)}/payments/${encodeURIComponent(paymentId)}/refund`,
        input,
      ),
    async listReconciliationIssues(id, status) {
      return (
        await request<{ issues: ReconciliationIssue[] }>(
          "GET",
          `${ws(id)}/payments/reconciliation-issues${status ? `?status=${status}` : ""}`,
        )
      ).issues;
    },
    resolveReconciliationIssue: (id, issueId, note) =>
      request<ReconciliationIssue>(
        "POST",
        `${ws(id)}/payments/reconciliation-issues/${encodeURIComponent(issueId)}/resolve`,
        note ? { note } : {},
      ),
    async listContactQuotes(id, contactId) {
      return (
        await request<{ quotes: QuoteSummary[] }>(
          "GET",
          `${ws(id)}/quotes?contactId=${encodeURIComponent(contactId)}`,
        )
      ).quotes;
    },
    listQuotes(id, query) {
      const p = new URLSearchParams();
      if (query?.status?.length) p.set("status", query.status.join(","));
      if (query?.q) p.set("q", query.q);
      if (query?.contactIds?.length) p.set("contactIds", query.contactIds.join(","));
      if (query?.limit) p.set("limit", String(query.limit));
      if (query?.cursor) p.set("cursor", query.cursor);
      const qs = p.toString();
      return request<QuoteListPage>("GET", `${ws(id)}/quotes${qs ? `?${qs}` : ""}`);
    },
    quotePdfUrl: (id, quoteId) => `${base}${ws(id)}/quotes/${encodeURIComponent(quoteId)}/pdf`,
    quoteDocumentLink: (id, quoteId) =>
      request<{ url: string }>(
        "POST",
        `${ws(id)}/quotes/${encodeURIComponent(quoteId)}/document-link`,
        {},
      ),
    async resendQuote(id, quoteId) {
      return (
        await request<{ quote: QuoteSummary }>(
          "POST",
          `${ws(id)}/quotes/${encodeURIComponent(quoteId)}/send`,
          { redeliver: true },
        )
      ).quote;
    },
    async transitionQuote(id, quoteId, to) {
      return (
        await request<{ quote: QuoteSummary }>(
          "POST",
          `${ws(id)}/quotes/${encodeURIComponent(quoteId)}/transition`,
          { to },
        )
      ).quote;
    },
    async getQuote(id, quoteId) {
      return (
        await request<{ quote: QuoteSummary }>(
          "GET",
          `${ws(id)}/quotes/${encodeURIComponent(quoteId)}`,
        )
      ).quote;
    },
    async applyQuoteDiscount(id, quoteId, input) {
      return (
        await request<{ quote: QuoteSummary }>(
          "POST",
          `${ws(id)}/quotes/${encodeURIComponent(quoteId)}/discount`,
          input,
        )
      ).quote;
    },
    async decideQuoteDiscount(id, quoteId, decision, note) {
      return (
        await request<{ quote: QuoteSummary }>(
          "POST",
          `${ws(id)}/quotes/${encodeURIComponent(quoteId)}/${decision}`,
          note ? { note } : {},
        )
      ).quote;
    },
    async listQuoteApprovals(id) {
      return (await request<{ approvals: QuoteApprovalItem[] }>("GET", `${ws(id)}/quote-approvals`))
        .approvals;
    },
    async quoteFromCart(id, cartId) {
      return (
        await request<{ quote: QuoteSummary }>(
          "POST",
          `${ws(id)}/quotes/from-cart/${encodeURIComponent(cartId)}`,
          {},
        )
      ).quote;
    },
    async addQuoteItem(id, quoteId, input) {
      return (
        await request<{ quote: QuoteSummary }>(
          "POST",
          `${ws(id)}/quotes/${encodeURIComponent(quoteId)}/items`,
          input,
        )
      ).quote;
    },
    async updateQuoteItem(id, quoteId, itemId, patch) {
      return (
        await request<{ quote: QuoteSummary }>(
          "PATCH",
          `${ws(id)}/quotes/${encodeURIComponent(quoteId)}/items/${encodeURIComponent(itemId)}`,
          patch,
        )
      ).quote;
    },
    async removeQuoteItem(id, quoteId, itemId) {
      return (
        await request<{ quote: QuoteSummary }>(
          "DELETE",
          `${ws(id)}/quotes/${encodeURIComponent(quoteId)}/items/${encodeURIComponent(itemId)}`,
        )
      ).quote;
    },
    async updateQuote(id, quoteId, patch) {
      return (
        await request<{ quote: QuoteSummary }>(
          "PATCH",
          `${ws(id)}/quotes/${encodeURIComponent(quoteId)}`,
          patch,
        )
      ).quote;
    },
    async listEcommerceAutomations(id) {
      return (
        await request<{ items: EcommercePackItem[] }>("GET", `${ws(id)}/ecommerce-automations`)
      ).items;
    },
    async setEcommerceAutomation(id, key, enabled) {
      await request("PUT", `${ws(id)}/ecommerce-automations/${key}`, { enabled });
    },
    async listOrderNotificationConfigs(id) {
      return (
        await request<{ configs: OrderNotificationConfig[] }>(
          "GET",
          `${ws(id)}/order-notifications`,
        )
      ).configs;
    },
    async saveOrderNotificationConfig(id, config) {
      return (
        await request<{ config: OrderNotificationConfig }>(
          "PUT",
          `${ws(id)}/order-notifications`,
          config,
        )
      ).config;
    },
    async getOrderLifecycleSettings(id) {
      return (
        await request<{ settings: OrderLifecycleSettings }>(
          "GET",
          `${ws(id)}/order-lifecycle-settings`,
        )
      ).settings;
    },
    async updateOrderLifecycleSettings(id, patch) {
      return (
        await request<{ settings: OrderLifecycleSettings }>(
          "PATCH",
          `${ws(id)}/order-lifecycle-settings`,
          patch,
        )
      ).settings;
    },
    async getQuoteSettings(id) {
      return (await request<{ settings: QuoteSettings }>("GET", `${ws(id)}/quote-settings`))
        .settings;
    },
    async updateQuoteSettings(id, patch) {
      return (
        await request<{ settings: QuoteSettings }>("PATCH", `${ws(id)}/quote-settings`, patch)
      ).settings;
    },
    async saveCartItemForLater(id, cartId, itemId) {
      return (
        await request<{ item: CartLine }>(
          "POST",
          `${ws(id)}/carts/${encodeURIComponent(cartId)}/items/${encodeURIComponent(itemId)}/save-for-later`,
        )
      ).item;
    },
    async restoreCartItem(id, cartId, itemId) {
      return (
        await request<{ item: CartLine }>(
          "POST",
          `${ws(id)}/carts/${encodeURIComponent(cartId)}/items/${encodeURIComponent(itemId)}/restore`,
        )
      ).item;
    },
    async createAbTest(id, input) {
      return (
        await request<{ test: AbTestSummary }>("POST", `${ws(id)}/automation-ab-tests`, input)
      ).test;
    },
    async getAbTestResults(id, testId) {
      return (
        await request<{ results: AbTestResultsView }>(
          "GET",
          `${ws(id)}/automation-ab-tests/${encodeURIComponent(testId)}/results`,
        )
      ).results;
    },
    async promoteAbTestWinner(id, testId, variant) {
      return (
        await request<{ result: { belowSampleSize: boolean } }>(
          "POST",
          `${ws(id)}/automation-ab-tests/${encodeURIComponent(testId)}/promote`,
          { variant },
        )
      ).result;
    },
    async removeCartItem(id, cartId, itemId) {
      await request<void>(
        "DELETE",
        `${ws(id)}/carts/${encodeURIComponent(cartId)}/items/${encodeURIComponent(itemId)}`,
      );
    },
    listPaymentMethods: async (id) =>
      (
        await request<{ paymentMethods: SavedCardInfo[] }>(
          "GET",
          `${ws(id)}/billing/payment-methods`,
        )
      ).paymentMethods,
    makePaymentMethodDefault: async (id, cardId) => {
      await request<unknown>("POST", `${ws(id)}/billing/payment-methods/${cardId}/default`);
    },
    removePaymentMethod: async (id, cardId) => {
      await request<unknown>("DELETE", `${ws(id)}/billing/payment-methods/${cardId}`);
    },
  };
}
